import { Injectable } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { ComponentStore } from '@ngrx/component-store';
import { HttpUtilitiesService, IGenericCrudRequestConstructionParameters } from '@utilities/http-utilities.service';
import { catchError, EMPTY, MonoTypeOperatorFunction, Observable, pipe, switchMap, tap } from 'rxjs';
import { IAdminUser } from '../admin-user-group.interface';
import { AdminUsersService } from '../admin-users.service';

interface IAdminUsersState {
  data: IAdminUser[] | null;
  dataTotal: number;
  dataLoading: boolean;
  error: string | null;
}

const initialState: IAdminUsersState = {
  data: null,
  dataLoading: false,
  dataTotal: 0,
  error: null,
};

@Injectable()
export class AdminUsersTabStore extends ComponentStore<IAdminUsersState> {
  public readonly data$ = this.selectSignal((state: IAdminUsersState) => state.data);
  public readonly dataTotal$ = this.selectSignal((state: IAdminUsersState) => state.dataTotal);
  public readonly dataLoading$ = this.selectSignal((state: IAdminUsersState) => state.dataLoading);
  public readonly error$ = this.selectSignal((state: IAdminUsersState) => state.error);

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError(() => {
        this.patchState({
          data: this.get().data ?? [],
          dataLoading: false,
          dataTotal: this.get().data ? this.get().dataTotal : 0,
          error: errorMessage,
        });

        return EMPTY;
      }),
    );

  public readonly fetchUsers = this.effect((params$: Observable<IGenericCrudRequestConstructionParameters>) => {
    return params$.pipe(
      tap(() => this.patchState({ dataLoading: true, error: null })),
      switchMap((params) => {
        const httpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.adminUsersService.getUsers(httpParams).pipe(
          tap((response: IApiResponse<IAdminUser[]>) => {
            this.patchState({
              data: response.data ?? [],
              dataLoading: false,
              dataTotal: response.meta?.total ?? 0,
            });
          }),
          this.handleError('Failed to load users'),
        );
      }),
    );
  });

  constructor(
    private readonly adminUsersService: AdminUsersService,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super(initialState);
  }
}
